import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SortState } from '@/hooks/use-url-state';
import {
  create_catalog_product,
  fetch_catalog_counts,
  fetch_catalog_product,
  delete_catalog_image,
  fetch_catalog_products,
  fetch_catalog_vendor_count,
  update_catalog_product,
  add_catalog_image,
  reorder_catalog_images,
  set_catalog_commission,
} from './api';
import type { CatalogQuery, CreateCatalogInput, UpdateCatalogInput } from './types';

const LIST_KEY = 'admin-catalog-products';

const COUNTS_KEY = 'admin-catalog-counts';
const DETAIL_KEY = 'admin-catalog-product';

export function use_catalog_products(query: CatalogQuery, sort?: SortState) {
  return useQuery({
    queryKey: [LIST_KEY, query, sort],
    queryFn: () => fetch_catalog_products(query, sort),
    placeholderData: (prev) => prev,
  });
}

export function use_catalog_counts() {
  return useQuery({ queryKey: [COUNTS_KEY], queryFn: fetch_catalog_counts });
}

export function use_catalog_product(id: string | undefined) {
  return useQuery({
    queryKey: [DETAIL_KEY, id],
    queryFn: () => fetch_catalog_product(id as string),
    enabled: !!id,
  });
}

export function use_create_catalog_product() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCatalogInput) => create_catalog_product(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [LIST_KEY] });
      qc.invalidateQueries({ queryKey: [COUNTS_KEY] });
      qc.invalidateQueries({ queryKey: [DETAIL_KEY] });
    },
  });
}

export function use_update_catalog_product() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCatalogInput }) => update_catalog_product(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [LIST_KEY] });
      qc.invalidateQueries({ queryKey: [COUNTS_KEY] });
      qc.invalidateQueries({ queryKey: [DETAIL_KEY] });
    },
  });
}

/** Fetched only when a master is opened — the list would otherwise fan out per row. */
export function use_catalog_vendor_count(id: string | null) {
  return useQuery({
    queryKey: ['admin-catalog-vendor-count', id],
    queryFn: () => fetch_catalog_vendor_count(id as string),
    enabled: id !== null,
  });
}

export function use_add_catalog_image() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file, source_note }: { id: string; file: File; source_note?: string }) =>
      add_catalog_image(id, file, source_note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [LIST_KEY] });
      qc.invalidateQueries({ queryKey: [COUNTS_KEY] });
      qc.invalidateQueries({ queryKey: [DETAIL_KEY] });
    },
  });
}

export function use_delete_catalog_image() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, image_id }: { id: string; image_id: string }) => delete_catalog_image(id, image_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [LIST_KEY] });
      qc.invalidateQueries({ queryKey: [COUNTS_KEY] });
      qc.invalidateQueries({ queryKey: [DETAIL_KEY] });
    },
  });
}

export function use_reorder_catalog_images() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, image_ids }: { id: string; image_ids: string[] }) => reorder_catalog_images(id, image_ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [LIST_KEY] });
      qc.invalidateQueries({ queryKey: [COUNTS_KEY] });
      qc.invalidateQueries({ queryKey: [DETAIL_KEY] });
    },
  });
}

export function use_set_catalog_commission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      mode: 'RATE' | 'FLAT' | null;
      rate_pct?: number | null;
      amount_inr?: number | null;
    }) => set_catalog_commission(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [LIST_KEY] });
      qc.invalidateQueries({ queryKey: [COUNTS_KEY] });
      qc.invalidateQueries({ queryKey: [DETAIL_KEY] });
    },
  });
}
